import { Box, Grid2, TextField } from "@mui/material";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import useNavigationGuard from "../../../../hooks/useNavigationGuard";
import {
  createDrugTypeService,
  updateDrugTypeService,
} from "../../../../services/drugTypeManagementService";
import { notifyError, notifySuccess } from "../../../../utilities/helper";
import Loader from "../../../../components/Loader";
import { CreateHandleUnsaveNavigation } from "../../../../utilities/handleUnsaveNavigation";
import dayjs from "dayjs";
import { HandleUnsaveNavigationWithFun } from "../../../../utilities/handleUnsaveNavigationWithFun";

const DataCorrection = () => {
  const id = useLocation()?.state?.id;

  const [load, setLoad] = useState(false);
  const [subjectName, setSubjectName] = useState("");

  const navigate = useNavigate();

  const access_token = localStorage?.getItem("access_token");
  const role = localStorage.getItem("role");

  const handleUnsaveNavigation = CreateHandleUnsaveNavigation();
  const handleUnSaveWithFun = HandleUnsaveNavigationWithFun();

  const createDataCreationSchema = yup.object().shape({
    userName: yup
      .string()
      .required("User Name is required")
      .matches(
        /^[A-Za-z][A-Za-z0-9\s!@#$%^&*()_+=-]*$/,
        "User Name must not start with a number or special character"
      )
      .test(
        "has-letter",
        "User Name must contain at least one alphabet",
        (value) => /[A-Za-z]/.test(value || "")
      ),
    subjectNumber: yup.string().required("Subject Number is required"),
    lastUpdate: yup
      .mixed()
      .test("is-valid-date", "Last Update is required", (value) =>
        dayjs(value).isValid()
      )
      .nullable(),
    typeOfDataChanged: yup
      .string()
      .required("Type of Data Changed is required"),
    oldValue: yup.string().required("Old Value is required"),
    newValue: yup.string().required("New Value is required"),
    notes: yup.string().required("Notes are required"),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(createDataCreationSchema),
    defaultValues: {
      userName: "",
      typeOfDataChanged: "",
      lastUpdate: "",
      subjectNumber: "",
      newValue: "",
      notes: "",
      oldValue: "",
    },
  });

  useNavigationGuard(isDirty);

  const addCorrectionData = async (data) => {
    setLoad(true);
    const newData = {
      ...data,
    };

    try {
      if (!id) {
        const res = await createDrugTypeService(access_token, newData);
        if (res?.data?.status == 409) {
          setLoad(false);
          notifyError("Data Correction already exists !");
          return;
        } else if (res?.status == 200) {
          setLoad(false);
          notifySuccess("Data Correction is created successfully !");
          reset();
          navigate("/update-subject");
        }
      } else {
        const res = await updateDrugTypeService(access_token, newData, id);
        if (res?.status == 200) {
          setLoad(false);
          notifySuccess("Data Correction is Updated successfully !");
          reset();
          navigate("/update-subject");
        }
      }
    } catch (err) {
      setLoad(false);
      console.log(err.message);
      notifyError(err.message);
      if (err.status == 401) {
        navigate("/unAuthorize");
      }
    }
  };

  return (
    <>
      {load ? (
        <Loader />
      ) : (
        <div className="content-body">
          <p className="bread-crumbs-link-container">
            <button
              className="bread-crumbs-btn"
              onClick={(e) => handleUnsaveNavigation(e, isDirty, "", navigate)}
            >
              Home
            </button>{" "}
            |{" "}
            <button
              className="bread-crumbs-btn"
              onClick={(e) => {
                navigate("/subject");
              }}
            >
              Subject Management
            </button>
            {" | "}
            <button
              className="bread-crumbs-btn"
              onClick={(e) =>
                handleUnsaveNavigation(e, isDirty, "update-subject", navigate)
              }
            >
              {subjectName}
            </button>{" "}
            |{" "}
            <button className="bread-crumbs-btn" to="/subject-data-correction">
              Add Data Correction
            </button>
            -{" "}
            <b style={{ color: "gray" }}>
              {localStorage?.getItem("studyName")}
            </b>
          </p>
          <Grid2
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%", margin: "0 auto" }}
          >
            <Grid2 item md={12}>
              <p className="page-heading">Data Correction Details</p>
            </Grid2>
          </Grid2>

          <Box
            component="form"
            autoComplete="off"
            sx={{ height: "auto", width: "100%" }}
            onSubmit={handleSubmit(addCorrectionData)}
          >
            <Grid2
              container
              alignItems="center"
              justifyContent={"space-between"}
              sx={{ width: "100%", margin: "0 auto" }}
              gap={1}
            >
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">User</p>
                  <TextField
                    size="small"
                    fullWidth
                    disabled={true}
                    type={"text"}
                    {...register("userName")}
                  />
                </div>
                {errors.userName && (
                  <div className="error-container">
                    {errors.userName.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">Subject Number</p>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={true}
                    type={"text"}
                    {...register("subjectNumber")}
                  />
                </div>
                {errors.subjectNumber && (
                  <div className="error-container">
                    {errors.subjectNumber.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Last Update<span>*</span>
                  </p>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Controller
                      name="lastUpdate"
                      control={control}
                      rules={{ required: "Last update is required" }} // Optional validation rule
                      render={({ field }) => (
                        <DateTimePicker
                          sx={{ width: "100%" }}
                          value={field.value ? dayjs(field.value) : null}
                          onChange={(date) => field.onChange(date)}
                          format="DD-MMM-YYYY"
                          slotProps={{
                            textField: {
                              placeholder: "DD-MMM-YYYY",
                              sx: {
                                "& .MuiInputBase-root": { height: "40px" },
                              },
                              size: "small",
                              fullWidth: true,
                            },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </div>
                {errors.lastUpdate && (
                  <div className="error-container">
                    {errors.lastUpdate.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Type Of Data Changed
                    <span>*</span>
                  </p>
                  <TextField
                    fullWidth
                    size="small"
                    type={"text"}
                    {...register("typeOfDataChanged")}
                  />
                </div>
                {errors.typeOfDataChanged && (
                  <div className="error-container">
                    {errors.typeOfDataChanged.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Old Value
                    <span>*</span>
                  </p>
                  <TextField
                    fullWidth
                    size="small"
                    type={"text"}
                    {...register("oldValue")}
                  />
                </div>
                {errors.oldValue && (
                  <div className="error-container">
                    {errors.oldValue.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    New Value
                    <span>*</span>
                  </p>
                  <TextField
                    fullWidth
                    size="small"
                    type={"text"}
                    {...register("newValue")}
                  />
                </div>
                {errors.newValue && (
                  <div className="error-container">
                    {errors.newValue.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Notes
                    <span>*</span>
                  </p>
                  <TextField
                    fullWidth
                    size="small"
                    type={"text"}
                    {...register("notes")}
                  />
                </div>
                {errors.notes && (
                  <div className="error-container">{errors.notes.message}</div>
                )}
              </Grid2>
            </Grid2>

            <Grid2
              alignItems={"end"}
              justifyContent="end"
              container
              gap={3}
              sx={{ marginTop: "2%" }}
            >
              <Grid2 item md={6}>
                <button
                  type="button"
                  className="create-btn-outlined"
                  onClick={(e) => {
                    e.preventDefault();
                    handleUnSaveWithFun(
                      e,
                      isDirty,
                      () =>
                        navigate("/update-subject", {
                          state: {
                            id: id,
                          },
                        }),
                      () => {}
                    );
                  }}
                >
                  Cancel
                </button>
              </Grid2>
              <Grid2 item md={6}>
                <button type="submit" className="create-btn">
                  Save
                </button>
              </Grid2>
            </Grid2>
          </Box>
        </div>
      )}
    </>
  );
};

export default DataCorrection;
